import React, {useEffect, useRef} from 'react';
import {AssetType, BoxLayer, Shapes} from '@taopix/taopix-core';
import {ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import {useSelector} from 'react-redux';
import Project from './../Project/Project';
import {SelectedShapeSelector} from '../../../../Selectors';
import {useDragAndDropState} from '../../../Context/DragAndDrop/DragAndDropContext';
import {UserInterfaceActions} from '../../../Context/UserInterface/UserInterfaceActionTypes';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import {useExperience} from '../../../Experience/ExperienceProvider';
import SidebarContainer from '../Sidebar/SidebarContainer';

export type DesignerProps = {};

// eslint-disable-next-line no-empty-pattern
const Designer = ({}: DesignerProps) => {
  const ui = useUiState();

  const isSmallScreen = useRef(ui.state.isSmallScreen);
  const section = useRef(ui.state.palette.section);

  const {experience} = useExperience();

  const selectedItem = useSelector(SelectedShapeSelector);
  const dragAndDropState = useDragAndDropState();

  const newSelectedItem = (() => {
    if (!selectedItem) return {ref: -1} as ComputedPageItem;
    return selectedItem;
  })();

  useEffect(() => {
    isSmallScreen.current = ui.state.isSmallScreen;
  }, [ui.state.isSmallScreen]);

  useEffect(() => {
    section.current = ui.state.palette.section;
  }, [ui.state.palette.section]);

  const onChooseSection = (section: string | null): void => {
    ui.dispatch(UserInterfaceActions.setToolPaletteSection(section));
  };

  useEffect(() => {
    // If no item selected, or we're on small screen, do nothing
    if (!selectedItem) {
      // Close any open item-related tips
      if (ui.state.tips.selectPictureForEmptyBox) {
        ui.dispatch(
          UserInterfaceActions.setTipOpen({name: 'selectPictureForEmptyBox', open: false})
        );
      }
      return;
    }

    if (null !== dragAndDropState.state.data) {
      // Bail during a drag action.
      return;
    }

    // Selecting a picture box
    if (
      Shapes.isImage(selectedItem) &&
      selectedItem.layer === BoxLayer.Normal &&
      [AssetType.User, AssetType.Productcollection].includes(selectedItem.hiresfiletype)
    ) {
      // If this is an empty picture box, show the 'Select a picture' tip/
      // Only shows on small screen if there are no palettes open.

      if (isSmallScreen.current && !section.current && selectedItem.hirespath === '') {
        // Show the 'Select a picture' tip
        ui.dispatch(
          UserInterfaceActions.setTipOpen({
            name: 'selectPictureForEmptyBox',
            open: true,
          })
        );
      }

      // Large screen: If we're not currently in the mask or box settings palette, switch to the pictures palette
      if (
        !isSmallScreen.current &&
        [''].includes(section.current ?? '') &&
        experience.EDITOR.pictures.picturePalette
      ) {
        // If selecting a picture box, and we're not currently in the backgrounds, layouts, masks or box settings palette, switch to the pictures palette
        onChooseSection('pictures');
      }
    }
  }, [isSmallScreen, section, selectedItem]);

  return (
    <div className="flex flex-1 max-w-full max-h-full min-h-0 sm:flex-col relative">
      <SidebarContainer selectedItem={newSelectedItem} />
      <Project selectedItem={newSelectedItem} />
    </div>
  );
};

export default Designer;
